import { ColumnDef } from '@tanstack/react-table';
import { SortButton, Typography } from '@wisecare-tech/design-system-web';
import { DiagnosisReportListRecords } from '~/domain/usecases/diagnosisReport/remote';
import { IconFlag } from '~/presentation/base/icons';
import history from '~/routes/History';
import { getProfessionalInfo } from '~/utils/getProfessionalInfo';
import { priorityMap } from '~/utils/mapPriority';
import {
  calculateAgeLaudo,
  formatISODateTimeLaudo,
} from '~/utils/tableDateLaudo';
import Actions from './actions';
import { Information, Priority } from './styles';
import { findLastOccurrence } from '~/utils/laudoLastOccurrence';

export const columns_laudando: ColumnDef<DiagnosisReportListRecords>[] = [
  {
    accessorKey: 'consultant',
    header: ({ column }) => <SortButton column={column}>Paciente</SortButton>,
    cell: ({ row }) => {
      const data = row.getValue(
        'consultant',
      ) as DiagnosisReportListRecords['consultant'];

      const value = row.original;

      const consultantName = data ? `${data?.firstName} ${data?.lastName}` : '';

      const consultantExternal = value?.consultantExternal
        ? `${value?.consultantExternal?.fullname}`
        : '';

      return (
        <Information>
          <Typography variant="b4_14regular">
            {consultantName.length > 0 ? consultantName : consultantExternal}
          </Typography>
          <Typography variant="b4_14regular" style={{ color: '#656A6E' }}>
            {calculateAgeLaudo(
              data?.birthdate ?? value?.consultantExternal?.birthdate,
            )}
          </Typography>
        </Information>
      );
    },
    sortingFn: (a, b) => {
      const consultantA = a.original
        .consultant as DiagnosisReportListRecords['consultant'];
      const consultantB = b.original
        .consultant as DiagnosisReportListRecords['consultant'];

      const consultantExternalA = a.original
        .consultantExternal as DiagnosisReportListRecords['consultantExternal'];
      const consultantExternalB = b.original
        .consultantExternal as DiagnosisReportListRecords['consultantExternal'];

      const consultantNameA = consultantA
        ? `${consultantA?.firstName} ${consultantA?.lastName}`
        : '';
      const consultantNameB = consultantB
        ? `${consultantB?.firstName} ${consultantB?.lastName}`
        : '';

      return consultantNameA.localeCompare(consultantNameB);
    },
  },
  {
    accessorKey: 'modality',
    header: ({ column }) => {
      return <SortButton column={column}>Modalidade do laudo</SortButton>;
    },
    cell: ({ row }) => {
      const modality = row.getValue(
        'modality',
      ) as DiagnosisReportListRecords['modality'];

      return <Typography variant="b4_14regular">{modality}</Typography>;
    },
  },
  {
    accessorKey: 'requester',
    header: ({ column }) => (
      <SortButton column={column}>Solicitante</SortButton>
    ),
    cell: ({ row }) => {
      const requester = row.getValue(
        'requester',
      ) as DiagnosisReportListRecords['requester'];

      const rowOriginal = row.original as DiagnosisReportListRecords;

      return (
        <Information>
          <Typography variant="b4_14regular">{requester?.fullname}</Typography>
          <Typography variant="b4_14regular" style={{ color: '#656A6E' }}>
            {rowOriginal.healthUnit.name}
          </Typography>
        </Information>
      );
    },
    sortingFn: (a, b) => {
      const requesterA = a.original
        .requester as DiagnosisReportListRecords['requester'];
      const requesterB = b.original
        .requester as DiagnosisReportListRecords['requester'];

      return requesterA?.fullname.localeCompare(requesterB?.fullname);
    },
  },
  {
    accessorKey: 'specialist',
    header: ({ column }) => (
      <SortButton column={column}>Designado para</SortButton>
    ),
    cell: ({ row }) => {
      const specialist = row.getValue(
        'specialist',
      ) as DiagnosisReportListRecords['specialist'];

      const rowOriginal = row.original as DiagnosisReportListRecords;

      return (
        <Information>
          <Typography variant="b4_14regular">
            {specialist?.fullname ?? '-'}
          </Typography>
          <Typography variant="b4_14regular" style={{ color: '#656A6E' }}>
            {rowOriginal?.specialty.name}
          </Typography>
        </Information>
      );
    },
    sortingFn: (a, b) => {
      const specialistA = a.original
        .specialist as DiagnosisReportListRecords['specialist'];
      const specialistB = b.original
        .specialist as DiagnosisReportListRecords['specialist'];

      return specialistA?.fullname.localeCompare(specialistB?.fullname);
    },
  },
  {
    accessorKey: 'history',
    header: ({ column }) => (
      <SortButton column={column}>Última atualização</SortButton>
    ),
    cell: ({ row }) => {
      const history = row.getValue(
        'history',
      ) as DiagnosisReportListRecords['history'];

      const lastOccurence = findLastOccurrence(history, 'REPORTING');

      const lastOccurrenceDate = formatISODateTimeLaudo(
        lastOccurence?.timestamp,
      );

      return (
        <Information>
          <Typography variant="b4_14regular">
            {lastOccurrenceDate.date}
          </Typography>
          <Typography variant="b4_14regular" style={{ color: '#656A6E' }}>
            {lastOccurrenceDate.hour}
          </Typography>
        </Information>
      );
    },
    sortingFn: (a, b) => {
      const historyA = a.original
        .history as DiagnosisReportListRecords['history'];
      const historyB = b.original
        .history as DiagnosisReportListRecords['history'];

      const lastOccurrenceA = findLastOccurrence(historyA, 'REPORTING');
      const lastOccurrenceB = findLastOccurrence(historyB, 'REPORTING');

      return (
        new Date(lastOccurrenceA?.timestamp).getTime() -
        new Date(lastOccurrenceB?.timestamp).getTime()
      );
    },
  },
  {
    accessorKey: 'priority',
    header: ({ column }) => <SortButton column={column}>Prioridade</SortButton>,
    cell: ({ row }) => {
      const priority = row.getValue(
        'priority',
      ) as DiagnosisReportListRecords['priority'];

      return (
        <Priority priority={priority}>
          <IconFlag />
          {priorityMap(priority)}
        </Priority>
      );
    },
    sortingFn: (a, b) => {
      const priorityA = a.original
        .priority as DiagnosisReportListRecords['priority'];
      const priorityB = b.original
        .priority as DiagnosisReportListRecords['priority'];

      return priorityMap(priorityA).localeCompare(priorityMap(priorityB));
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const professional_id = getProfessionalInfo();

      const handleGoToDetails = (id: number) => {
        history.push(`/diagnosis/report/${id}`);
      };

      const value = row.original;

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Actions
            documentId={value.id}
            actions={value.actions}
            currentStatus={value.status}
          />
        </div>
      );
    },
  },
];
